import type { SettlementDocument } from '@/types/settlement-documents'

/** Canonical base ladder (frame + ordering). Faktura → Påminnelse → Inkasso → Kronofogden. */
export const STAGE_ORDER = ['invoice', 'reminder', 'debt_collection', 'enforcement'] as const
export type LifecycleStage = (typeof STAGE_ORDER)[number]

/** Documents that render a lifecycle timeline (vs amortization/umbrella/dispute cards). */
export function isLifecycleDocument(document: SettlementDocument): boolean {
  return (
    document.reference === 'case' || document.reference === 'debt_collection' || document.reference === 'enforcement'
  )
}

// Cancelled amortization plans get status 'cancelled_closed' (not 'closed') plus the same
// closed{} payload (ada-api cancel_amortization) — they must partition as closed too (R7).
const CLOSED_STATUSES = new Set(['closed', 'cancelled_closed'])

/** A settled/closed document (mirrors legacy `is_closed`): shown green as "Avslutat", no timeline. */
export function isClosedDocument(document: SettlementDocument): boolean {
  return document.closed != null && CLOSED_STATUSES.has(document.status ?? '')
}

/**
 * What to pay now for a document. FLAG-driven: a closed case is 0 regardless of the remaining
 * field — only "paid" closes zero the field in the DB, so an unpaid/force close keeps a non-zero
 * remaining that must not read as "to pay".
 *
 * Lives here rather than in `useDebtOverview` because the payment dialog needs the same rule for
 * a SINGLE document and must not run the overview's six-query fan-out to price it — least of all
 * on the login screen, where the dialog is mounted while logged out (ADA-4432).
 */
export function toPayNow(document: SettlementDocument): number {
  return isClosedDocument(document) ? 0 : (document.total_remaining_transaction_amount ?? 0)
}

/**
 * The due date to DISPLAY for a document (ADA-4434), from the ADA-4471 `due_date` stamp: a granted
 * respite overrides the claim letter's date while set and later. Returns null when the document
 * carries no stamp (chains untouched since the API go-live — there is no backfill, and a respite
 * alone does not seed the stamp on an unstamped chain), so callers fall back to the print/OCR-derived
 * date. The respite-without-letter branch is kept as a defensive guard for stamps that carry only a
 * respite date. Shared by the overview card and the case view.
 *
 * API datetimes are RFC 1123 strings ("Tue, 14 Jan 2026 …") — compared as Dates, never lexically.
 */
export function effectiveDueDate(document: SettlementDocument): string | null {
  const dueDateStamp = document.due_date
  if (dueDateStamp == null) return null
  if (
    dueDateStamp.respite_date != null &&
    (dueDateStamp.due_date == null || new Date(dueDateStamp.respite_date) > new Date(dueDateStamp.due_date))
  ) {
    return dueDateStamp.respite_date
  }
  return dueDateStamp.due_date ?? null
}

/**
 * Collapse any raw state/status value into a canonical lifecycle stage. Sub-steps fold into
 * their stage; branch/side states (dispute/debt_surveillance/estate/restructuring/amortization)
 * are absent → stageOf returns null.
 *
 * Keyed by the API's own `state` AND `status` values — the vocabularies overlap lexically
 * (e.g. `reminder` is both), and status is the finer sub-step within a state, so one map
 * covers both. Verified 2026-07-24 against ada-api configuration/__init__.py:
 *  - CaseState values (state)
 *  - *_ONLY_STATUSES → CASE/DEBT_COLLECTION/ENFORCEMENT_STATUSES (status sub-steps)
 * A few flow-item values (schedule_*, evaluate_*) remain from the earlier next_flow_item
 * mapping — harmless, and future-proof should they ever surface as statuses.
 * This map is the single place to extend when new states/statuses are added.
 */
const STAGE_BY_STATE_OR_STATUS: Record<string, LifecycleStage> = {
  // invoice stage
  invoice: 'invoice',
  parking_ticket: 'invoice',
  // reminder stage
  soft_reminder: 'reminder',
  reminder: 'reminder',
  schedule_reminder: 'reminder',
  evaluate_advice_reminder: 'reminder',
  residual_claim: 'reminder',
  foreign_reminder: 'reminder',
  foreign_residual_claim: 'reminder',
  // debt collection stage
  debt_collection: 'debt_collection',
  debt_collection_residual_claim: 'debt_collection',
  extra_debt_collection: 'debt_collection',
  foreign_debt_collection: 'debt_collection',
  foreign_debt_collection_residual_claim: 'debt_collection',
  extra_foreign_debt_collection: 'debt_collection',
  restructuring: 'debt_collection',
  // enforcement stage
  verdict_submission: 'enforcement',
  order_to_pay: 'enforcement',
  verdict: 'enforcement',
  enforcement_submission: 'enforcement',
  schedule_enforcement: 'enforcement',
  enforcement: 'enforcement',
}

/**
 * Branch/side tracks (mixed `state` and `status` values — checked against both fields): when a
 * lifecycle document is CURRENTLY in one of these, it has left the linear ladder
 * (dispute/surveillance/estate/restructuring), so no timeline is drawn (D13; those render their
 * own cards). Note: `debt_restructuring` is a branch state, but the `restructuring` status is a
 * debt-collection sub-step — hence they map differently.
 */
const BRANCH_STATES_OR_STATUSES = new Set<string>([
  'dispute',
  'legal_conciliation',
  'debt_restructuring',
  'initiated_debt_restructuring',
  'reconstruction_declared',
  'debt_surveillance',
  'estate',
  'estate_surveillance',
  'estate_claim',
  'bankruptcy_estate',
  'bankruptcy_surveillance',
])

/** Map a raw `state`/`status` value to its canonical lifecycle stage (null for branch/side states). */
export function stageOf(stateOrStatus: string | undefined | null): LifecycleStage | null {
  return stateOrStatus == null ? null : (STAGE_BY_STATE_OR_STATUS[stateOrStatus] ?? null)
}

export type NodeStatus = 'passed' | 'current'
export interface TimelineNode {
  stage: LifecycleStage
  status: NodeStatus
  date?: string // state_date from history; the invoice node may carry cases.debt.invoice_date
}

/**
 * Build the timeline for a lifecycle document (PO-beslut 2026-07-27 — revised D13).
 * Only what actually happened is rendered:
 *  - passed:  tier proven by state_history (earliest state_date wins)
 *  - current: tier of live state/status
 *  - the Faktura node is ALWAYS included — every case is based on an invoice, but only
 *    invoice-registration cases ever record an `invoice` state in history (case
 *    registrations start at reminder/debt_collection). Its date falls back to the
 *    supplied `invoiceDate` (cases.debt.invoice_date — required + customer_user-readable;
 *    DC/enforcement documents carry no invoice data, so the caller resolves it via the parent case).
 * No planned/skipped/future nodes: upcoming steps are never speculated, and base tiers
 * without proof are simply not rendered. A node is NEVER 'passed' without evidence.
 *
 * Returns null when the document is not a lifecycle document, is closed, or is currently
 * in a branch state (dispute/surveillance/estate/restructuring) — those have no linear
 * timeline. Consumed by the case view (ADA-4431); the overview cards no longer render it.
 */
export function buildTimeline(document: SettlementDocument, invoiceDate?: string): TimelineNode[] | null {
  if (!isLifecycleDocument(document)) return null
  // Closed documents show no timeline (legacy parity) — they render as a green "Avslutat" card.
  if (isClosedDocument(document)) return null
  // Currently on a branch (dispute/surveillance/estate/restructuring) → no linear timeline.
  if (BRANCH_STATES_OR_STATUSES.has(document.status ?? '') || BRANCH_STATES_OR_STATUSES.has(document.state ?? ''))
    return null

  const passedDate = new Map<LifecycleStage, string>()
  for (const historyItem of document.state_history ?? []) {
    const stage = stageOf(historyItem.state)
    if (stage == null) continue
    // Keep the EARLIEST date per stage without assuming history order. state_date is an
    // RFC 1123 string — compare as timestamps, never lexically.
    const existing = passedDate.get(stage)
    if (existing == null || new Date(historyItem.state_date).getTime() < new Date(existing).getTime()) {
      passedDate.set(stage, historyItem.state_date)
    }
  }
  // status wins (parking_ticket/invoice safeguards live on status), then state, then discriminator.
  const currentStage = stageOf(document.status) ?? stageOf(document.state) ?? stageOf(document.reference)

  return STAGE_ORDER.filter((stage) => stage === 'invoice' || stage === currentStage || passedDate.has(stage)).map(
    (stage): TimelineNode => ({
      stage,
      status: stage === currentStage ? 'current' : 'passed',
      date: passedDate.get(stage) ?? (stage === 'invoice' ? invoiceDate : undefined),
    })
  )
}

/** Current lifecycle stage of a document (for the reference chip / card tint; no timeline build). */
export function documentStage(document: SettlementDocument): LifecycleStage | null {
  return stageOf(document.status) ?? stageOf(document.state) ?? stageOf(document.reference)
}

/**
 * Vuetify theme color per lifecycle stage (mirrors legacy style_mixin.reference_color:
 * invoice=grey, reminder=blue, debt_collection=orange, enforcement=red). Palette migrated to the
 * Vuetify theme in ADA-4410. The component picks the _main/_light/_dark context.
 */
export const STAGE_COLOR: Record<LifecycleStage, string> = {
  invoice: 'grey',
  reminder: 'blue',
  debt_collection: 'orange',
  enforcement: 'red',
}
